import * as Yup from 'yup';
import { AddressFindData } from './api.v1.address-find';
import {
  AddressOption,
  AutoComplete,
  AutoCompleteOptionType,
  Button,
  Checkbox,
  ColorName,
  ConfirmationCodeInput,
  IconCalendar24,
  IconChevron,
  IconSearch16,
  InstantVerificationManuallyAddress,
  InstantVerificationProgress,
  ManualAddress,
  Modal,
  OutlinedInput,
  ReadOnlyTextarea,
  Role,
  Row,
  Spinner,
  Typography,
  useTheme,
} from '@copybet/wui';
import { AddressRetrieveData } from './api.v1.address-retrieve';
import { authenticator } from '../services/auth.server';
import { AUTHORIZED_INDEX_PATH, LOADING_STATES, USER_SESSION_ERROR_KEY } from '../utils/consts';
import { commitSession, getSession } from '../services/session.server';
import { createOperationWithGraphqlContext } from '../utils/createLoader';
import { DTOQuery } from '@copybet/dto';
import { Form, Link, useBlocker, useLoaderData, useNavigate, useSearchParams } from '@remix-run/react';
import { format } from 'date-fns';
import { gql } from '@copybet/apollo';
import { isMobileUserAgent } from '../utils/isMobileUserAgent';
import { isSignUpFormDirty } from '../utils/sign-up-form';
import { Item } from '@copybet/loqate';
import { json, LoaderFunction, redirect } from '../config';
import { normalizePhone } from '@copybet/core';
import { toast } from 'sonner';
import { toIsoBirthDate } from '../utils/toIsoBirthDate';
import { Trans, useTranslation } from 'react-i18next';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFetcherWithPromise } from '../utils/useFetcherWithPromise';
import { useFetcherWithReset } from '../utils/useFetcherWithReset';
import { useMobileDetector } from '../utils/useMobileDetector';
import { VerificationRequiredIcon } from '../shared/verification-required-icon';
import { WidgetStack } from '../shared/components/widget-stack';
import emailValidator from '../validators/emailValidator';
import gbgValidator, { ADDRESS_Q_MIN_LENGTH, gbgManualAddress, getBirthMaxDate, getBirthMinDate } from '../validators/gbgValidator';
import invariant from 'tiny-invariant';
import ReCAPTCHA from 'react-google-recaptcha';
import styled from '@emotion/styled';

type SendCodeResponse = {
  success?: boolean;
  errors?: Record<string, string | undefined>;
};

type ConfirmCodeResponse = SendCodeResponse & {
  confirmationHash?: string | null;
};

type RegistrationResult = {
  isVerified: boolean;
  GBGStatus?: string | null;
};

type RegistrationResponse = {
  data?: RegistrationResult;
  errors?: Record<string, string | undefined>;
};

const COUNTDOWN_SECONDS = 60;
const ATTRIBUTION_STORAGE_KEY = 'attribution_params';
const TRACKING_PARAM_NAMES = [
  'appsflyerUid',
  'isRegistration',
  'utmMedium',
  'utmTerm',
  'utmContent',
  'referrer',
  'utmCampaign',
  'bTag',
  'mediaSource',
  'affiliateCode',
  'utmSource',
  'twclid',
] as const;
const BIRTH_DATE_SEGMENT_ORDER = ['day', 'month', 'year'] as const;

type BirthDateSegmentKey = (typeof BIRTH_DATE_SEGMENT_ORDER)[number];
type BirthDateSegments = Record<BirthDateSegmentKey, string>;

const BIRTH_DATE_SEGMENT_LENGTHS: Record<BirthDateSegmentKey, number> = {
  day: 2,
  month: 2,
  year: 4,
};

const sanitizePhoneInput = (value: string) => value.replace(/[^\d+\s()-]/g, '');

const sanitizeBirthDateSegment = (value: string, maxLength: number) => value.replace(/\D/g, '').slice(0, maxLength);

const formatBirthDateSegments = ({ day, month, year }: BirthDateSegments) => {
  const rawValue = [day, month, year].join('/');
  return rawValue === '//' ? '' : rawValue.replace(/\/+$/, '');
};

const getAdjacentBirthDateSegment = (segment: BirthDateSegmentKey, direction: 'next' | 'previous') => {
  const currentIndex = BIRTH_DATE_SEGMENT_ORDER.indexOf(segment);
  const adjacentIndex = direction === 'next' ? currentIndex + 1 : currentIndex - 1;
  return BIRTH_DATE_SEGMENT_ORDER[adjacentIndex];
};

const MobileBirthDateInput = styled(OutlinedInput)`
  input {
    appearance: none;
    box-sizing: border-box;
    display: block;
    height: 52px;
    padding-right: 48px;
    color-scheme: ${({ theme }) => theme.models.theme.name};

    &::-webkit-date-and-time-value {
      display: block;
      min-height: 1lh;
      margin: 0;
      text-align: left;
    }

    &::-webkit-datetime-edit {
      padding: 0;
    }

    &::-webkit-calendar-picker-indicator {
      display: none;
    }

    /* WebKit otherwise shows today's date even when the field is empty. */
    &:not(:focus)::-webkit-datetime-edit {
      opacity: ${({ value }) => (value ? 1 : 0)};
    }
  }
`;

const MobileBirthDateField = styled.div`
  position: relative;
  margin-bottom: 16px;
`;

const MobileBirthDateIcon = styled(IconCalendar24)`
  position: absolute;
  top: 27px;
  right: 16px;
  transform: translateY(-50%);
  color: ${({ theme }) => theme.colors.$label02};
  pointer-events: none;
`;

const DesktopBirthDateFieldWrapper = styled.div`
  margin-bottom: 16px;
`;

const DesktopBirthDateField = styled.div<{ $disabled: boolean; $error: boolean; $focused: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  padding: 23px 15px 7px;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.colors.$label02};
  background-color: ${({ theme }) => theme.colors.$ui03};
  color: ${({ theme }) => theme.colors.$label02};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'text')};
  transition: border-color 200ms cubic-bezier(0, 0, 0.2, 1) 0ms;

  ${({ $focused, theme }) => ($focused ? `border-color: ${theme.colors.$label01};` : '')}
  ${({ $disabled, theme }) => ($disabled ? `border-color: ${theme.colors.$label03};` : '')}
  ${({ $error, theme }) => ($error ? `border-color: ${theme.colors.$red};` : '')}
`;

const DesktopBirthDateLabel = styled.label<{ $error: boolean }>`
  position: absolute;
  top: 5px;
  left: 15px;
  ${({ theme }) => theme.components?.typography?.variants['$body02']}
  transform: scale(0.75);
  transform-origin: top left;
  pointer-events: none;
  color: ${({ $error, theme }) => ($error ? theme.colors.$red : theme.colors.$label02)};
`;

const DesktopBirthDateInputs = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 1;
  min-width: 0;
`;

const DesktopBirthDateSegmentInput = styled.input<{ $disabled: boolean; $filled: boolean; $segment: BirthDateSegmentKey }>`
  width: ${({ $segment }) => ($segment === 'year' ? '4.4ch' : '2.4ch')};
  padding: 0;
  border: 0;
  outline: 0;
  background: transparent;
  font: inherit;
  letter-spacing: inherit;
  color: ${({ $disabled, $filled, theme }) => ($disabled ? theme.colors.$label02 : $filled ? theme.colors.$label01 : theme.colors.$label02)};
  caret-color: ${({ $disabled, theme }) => ($disabled ? 'transparent' : theme.colors.$label01)};

  &::placeholder {
    color: ${({ theme }) => theme.colors.$label02};
    opacity: 1;
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

const DesktopBirthDateDivider = styled.span`
  color: ${({ theme }) => theme.colors.$label02};
  user-select: none;
`;

const DesktopBirthDateIcon = styled.div`
  display: flex;
  align-items: center;
  margin-left: auto;
  flex-shrink: 0;
  pointer-events: none;
`;

const DesktopBirthDateError = styled(Typography)`
  min-height: 16px;
  margin-top: 16px;
  padding: 0 16px;
`;

type DesktopBirthDateInputProps = {
  id: string;
  name: string;
  label: string;
  errorText?: string;
  disabled?: boolean;
  calendarIcon?: React.ReactNode;
  onChange?: (value: string) => void;
};

function DesktopBirthDateInput({ id, name, label, errorText = '', disabled = false, calendarIcon, onChange }: DesktopBirthDateInputProps) {
  const [segments, setSegments] = useState<BirthDateSegments>({ day: '', month: '', year: '' });
  const [focused, setFocused] = useState(false);
  const dayInputRef = useRef<HTMLInputElement | null>(null);
  const monthInputRef = useRef<HTMLInputElement | null>(null);
  const yearInputRef = useRef<HTMLInputElement | null>(null);
  const getSegmentForClientX = useCallback((clientX: number): BirthDateSegmentKey => {
    const segmentInputs: Array<{ segment: BirthDateSegmentKey; input: HTMLInputElement | null }> = [
      { segment: 'day', input: dayInputRef.current },
      { segment: 'month', input: monthInputRef.current },
      { segment: 'year', input: yearInputRef.current },
    ];

    for (const { segment, input } of segmentInputs) {
      if (!input) {
        continue;
      }

      const { left, width } = input.getBoundingClientRect();
      if (clientX <= left + width / 2) {
        return segment;
      }
    }

    return 'year';
  }, []);

  const focusSegment = useCallback((segment: BirthDateSegmentKey, selectValue = false) => {
    const refs: Record<BirthDateSegmentKey, HTMLInputElement | null> = {
      day: dayInputRef.current,
      month: monthInputRef.current,
      year: yearInputRef.current,
    };
    const input = refs[segment];
    if (!input) {
      return;
    }

    input.focus();
    if (selectValue) {
      input.select();
      return;
    }

    const caretPosition = input.value.length;
    input.setSelectionRange(caretPosition, caretPosition);
  }, []);

  const updateSegments = useCallback(
    (nextSegments: BirthDateSegments) => {
      setSegments(nextSegments);
      onChange?.(formatBirthDateSegments(nextSegments));
    },
    [onChange],
  );

  const handleSegmentChange = useCallback(
    (segment: BirthDateSegmentKey) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const maxLength = BIRTH_DATE_SEGMENT_LENGTHS[segment];
      const nextValue = sanitizeBirthDateSegment(event.target.value, maxLength);
      const nextSegments = {
        ...segments,
        [segment]: nextValue,
      };

      updateSegments(nextSegments);

      if (nextValue.length === maxLength) {
        const nextSegment = getAdjacentBirthDateSegment(segment, 'next');
        if (nextSegment) {
          window.requestAnimationFrame(() => focusSegment(nextSegment, true));
        }
      }
    },
    [focusSegment, segments, updateSegments],
  );

  const handleSegmentKeyDown = useCallback(
    (segment: BirthDateSegmentKey) => (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (disabled || event.ctrlKey || event.metaKey || event.altKey) {
        return;
      }

      const currentInput = event.currentTarget;

      if (event.key === 'Backspace') {
        if (currentInput.value.length === 0 && currentInput.selectionStart === 0 && currentInput.selectionEnd === 0) {
          const previousSegment = getAdjacentBirthDateSegment(segment, 'previous');
          if (previousSegment) {
            event.preventDefault();
            focusSegment(previousSegment, true);
          }
        }
        return;
      }

      if (event.key === 'ArrowLeft') {
        if ((currentInput.selectionStart ?? 0) === 0 && (currentInput.selectionEnd ?? 0) === 0) {
          const previousSegment = getAdjacentBirthDateSegment(segment, 'previous');
          if (previousSegment) {
            event.preventDefault();
            focusSegment(previousSegment);
          }
        }
        return;
      }

      if (event.key === 'ArrowRight') {
        if ((currentInput.selectionEnd ?? 0) === currentInput.value.length) {
          const nextSegment = getAdjacentBirthDateSegment(segment, 'next');
          if (nextSegment) {
            event.preventDefault();
            focusSegment(nextSegment, true);
          }
        }
        return;
      }

      if (event.key === '/' || event.key === '.' || event.key === '-') {
        const nextSegment = getAdjacentBirthDateSegment(segment, 'next');
        if (nextSegment) {
          event.preventDefault();
          focusSegment(nextSegment, true);
        }
        return;
      }

      if (event.key.length === 1 && !/\d/.test(event.key)) {
        event.preventDefault();
      }
    },
    [disabled, focusSegment],
  );

  const handleSegmentPaste = useCallback(
    (event: React.ClipboardEvent<HTMLInputElement>) => {
      const pastedDigits = event.clipboardData.getData('text').replace(/\D/g, '');

      if (pastedDigits.length !== 8) {
        return;
      }

      event.preventDefault();

      const nextSegments: BirthDateSegments = {
        day: pastedDigits.slice(0, 2),
        month: pastedDigits.slice(2, 4),
        year: pastedDigits.slice(4, 8),
      };

      updateSegments(nextSegments);
      window.requestAnimationFrame(() => focusSegment('year'));
    },
    [focusSegment, updateSegments],
  );

  const hiddenBirthDateValue = formatBirthDateSegments(segments);
  const hasError = errorText.length > 0;
  const handleFieldMouseDown = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (disabled || event.target instanceof HTMLInputElement) {
        return;
      }

      const firstUnfilledSegment = BIRTH_DATE_SEGMENT_ORDER.find((segment) => segments[segment].length < BIRTH_DATE_SEGMENT_LENGTHS[segment]);

      event.preventDefault();
      focusSegment(firstUnfilledSegment ?? getSegmentForClientX(event.clientX), true);
    },
    [disabled, focusSegment, getSegmentForClientX, segments],
  );

  return (
    <DesktopBirthDateFieldWrapper>
      <input id={id} name={name} type="hidden" value={hiddenBirthDateValue} readOnly />
      <DesktopBirthDateField
        $disabled={disabled}
        $error={hasError}
        $focused={focused}
        onFocusCapture={() => setFocused(true)}
        onBlurCapture={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
            setFocused(false);
          }
        }}
        onMouseDown={handleFieldMouseDown}>
        <DesktopBirthDateLabel $error={hasError} htmlFor={`${id}-day`}>
          {label}
        </DesktopBirthDateLabel>
        <DesktopBirthDateInputs>
          <DesktopBirthDateSegmentInput
            ref={dayInputRef}
            id={`${id}-day`}
            name={`${name}Day`}
            type="text"
            inputMode="numeric"
            autoComplete="bday-day"
            placeholder="DD"
            maxLength={BIRTH_DATE_SEGMENT_LENGTHS.day}
            value={segments.day}
            disabled={disabled}
            aria-label={`${label} DD`}
            $disabled={disabled}
            $filled={segments.day.length > 0}
            $segment="day"
            onChange={handleSegmentChange('day')}
            onKeyDown={handleSegmentKeyDown('day')}
            onPaste={handleSegmentPaste}
            onFocus={(event) => event.currentTarget.select()}
          />
          <DesktopBirthDateDivider>/</DesktopBirthDateDivider>
          <DesktopBirthDateSegmentInput
            ref={monthInputRef}
            id={`${id}-month`}
            name={`${name}Month`}
            type="text"
            inputMode="numeric"
            autoComplete="bday-month"
            placeholder="MM"
            maxLength={BIRTH_DATE_SEGMENT_LENGTHS.month}
            value={segments.month}
            disabled={disabled}
            aria-label={`${label} MM`}
            $disabled={disabled}
            $filled={segments.month.length > 0}
            $segment="month"
            onChange={handleSegmentChange('month')}
            onKeyDown={handleSegmentKeyDown('month')}
            onPaste={handleSegmentPaste}
            onFocus={(event) => event.currentTarget.select()}
          />
          <DesktopBirthDateDivider>/</DesktopBirthDateDivider>
          <DesktopBirthDateSegmentInput
            ref={yearInputRef}
            id={`${id}-year`}
            name={`${name}Year`}
            type="text"
            inputMode="numeric"
            autoComplete="bday-year"
            placeholder="YYYY"
            maxLength={BIRTH_DATE_SEGMENT_LENGTHS.year}
            value={segments.year}
            disabled={disabled}
            aria-label={`${label} YYYY`}
            $disabled={disabled}
            $filled={segments.year.length > 0}
            $segment="year"
            onChange={handleSegmentChange('year')}
            onKeyDown={handleSegmentKeyDown('year')}
            onPaste={handleSegmentPaste}
            onFocus={(event) => event.currentTarget.select()}
          />
        </DesktopBirthDateInputs>
        {calendarIcon ? <DesktopBirthDateIcon aria-hidden>{calendarIcon}</DesktopBirthDateIcon> : null}
      </DesktopBirthDateField>
      {hasError ? (
        <DesktopBirthDateError as="div" variant="$body01" color="$red" display="block">
          {errorText}
        </DesktopBirthDateError>
      ) : null}
    </DesktopBirthDateFieldWrapper>
  );
}

// const AddressActions = styled.div`
//   margin-top: 8px;
// `;

const LeaveModalActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 24px;
`;

const normalizeErrors = (errors?: Record<string, string | number | undefined>) => {
  if (!errors) {
    return undefined;
  }

  return Object.fromEntries(Object.entries(errors).map(([key, value]) => [key, value == null ? undefined : String(value)])) as Record<string, string | undefined>;
};

const getBuilding = (rAddress: AddressRetrieveData['retrievedData'] | undefined) => {
  if (!rAddress) {
    return '';
  }

  return [rAddress?.BuildingNumber, rAddress?.BuildingName, rAddress?.SubBuilding].filter((x) => Boolean(x))?.join(' ') || rAddress?.Line2 || '';
};

const getStreet = (rAddress: AddressRetrieveData['retrievedData'] | undefined) => {
  if (!rAddress) {
    return '';
  }

  return rAddress?.Street || rAddress?.Line2 || '';
};

const formatAddressText = (manualAddress: ManualAddress | null, retrievedAddress: AddressRetrieveData['retrievedData'] | undefined) => {
  if (manualAddress) {
    return [manualAddress.building, manualAddress.district, manualAddress.street, manualAddress.city, manualAddress.postalCode, manualAddress.country]
      .filter((x) => !!x && x.length > 0)
      .join('\n');
  }

  if (retrievedAddress) {
    return [
      getBuilding(retrievedAddress),
      getStreet(retrievedAddress),
      retrievedAddress?.District,
      retrievedAddress?.City,
      retrievedAddress?.PostalCode,
      retrievedAddress?.CountryName,
    ]
      .filter((x) => !!x && x.length > 0)
      .join('\n');
  }

  return undefined;
};

const getAddressDetails = (item: Item) => {
  if (!item?.Description) return [];
  return item.Type === 'Address' ? [item.Description] : item.Description.split('-').map((part) => part.trim());
};

const formatAddressOptions = (items: Item[]) =>
  items.map((item) => {
    const isAddress = item.Type === 'Address';
    const [addressPart, prototype] = getAddressDetails(item);
    return {
      label: (
        <Typography as="span">
          {item.Text}
          {addressPart && (
            <Typography as="span" color="$label02" style={{ paddingLeft: 6 }}>
              {addressPart}
            </Typography>
          )}
        </Typography>
      ),
      value: item.Id,
      item,
      selectable: isAddress,
      action: !isAddress
        ? {
            label: prototype,
            variant: 'ghost' as any,
            entIcon: <IconChevron dir="right" />,
            color: '$label02' as ColorName,
          }
        : undefined,
    };
  });

const getFormValue = (formData: FormData, name: string) => {
  const value = formData.get(name);
  return typeof value === 'string' ? value.trim() : '';
};

export const meta = () => [{ title: 'Create account | UK Sportsbook' }];

export const loader: LoaderFunction = async (args) => {
  const { request } = args;
  invariant(process.env.RECAPTCHA_SITE_KEY, 'RECAPTCHA_SITE_KEY must be set');
  const isMobileScreen = isMobileUserAgent(request.headers.get('User-Agent') || '');
  const authData = await authenticator.isAuthenticated(request);
  if (authData) {
    return createOperationWithGraphqlContext(args, async ({ client }) => {
      const query = await client.query<Pick<DTOQuery, 'getUserVerificationFlow' | 'getCurrentUser'>>({
        query: gql`
          query GetUserVerificationFlowQuery {
            getUserVerificationFlow {
              flow
            }
            getCurrentUser {
              isFullyVerified
            }
          }
        `,
        fetchPolicy: 'no-cache',
      });

      const isFullyVerified = query.data?.getCurrentUser?.isFullyVerified;

      if (isFullyVerified === false) {
        return redirect('/instant-verification/status');
      }

      return redirect(AUTHORIZED_INDEX_PATH);
    });
  }

  const session = await getSession(request.headers.get('Cookie'));
  const msg = await session.get(USER_SESSION_ERROR_KEY);
  let errors;
  if (msg && msg.message) {
    try {
      errors = JSON.parse(msg.message);
    } catch (e) {
      errors = { common: msg.message };
    }
  }

  session.unset(USER_SESSION_ERROR_KEY);
  return json(
    {
      errors,
      isMobileScreen,
      rkey: process.env.RECAPTCHA_SITE_KEY,
    },
    {
      headers: {
        'Set-Cookie': await commitSession(session),
      },
    },
  );
};

export default function SignUp() {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const loaderData = useLoaderData<typeof loader>();
  const { isMobileScreen } = useMobileDetector({ initialValue: loaderData.isMobileScreen });
  const recaptchaRef = useRef<ReCAPTCHA | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastConfirmedCodeRef = useRef<string | null>(null);
  const confirmInFlightRef = useRef(false);
  const pendingFormDataRef = useRef<FormData | null>(null);
  const newsletterSyncedRef = useRef(false);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | undefined>(undefined);
  const [codeError, setCodeError] = useState<string | undefined>(undefined);
  const [formErrors, setFormErrors] = useState<Record<string, string | undefined>>({});
  const [codeModalVisible, setCodeModalVisible] = useState(false);
  const [manualModalVisible, setManualModalVisible] = useState(false);
  const [confirmationCode, setConfirmationCode] = useState<string | null>(null);
  const [countDown, setCountDown] = useState(0);
  const [showProgress, setShowProgress] = useState(false);
  const [verificationResult, setVerificationResult] = useState<RegistrationResult | null>(null);

  const [addressOptions, setAddressOptions] = useState<AddressOption<Item>[]>([]);
  const [address, setAddress] = useState<AutoCompleteOptionType<Item> | null>(null);
  const [addressQ, setAddressQ] = useState<string>('');
  const [retrievedAddress, setRetrievedAddress] = useState<AddressRetrieveData['retrievedData'] | undefined>();
  const [manualAddress, setManualAddress] = useState<ManualAddress | null>(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [birthDate, setBirthDate] = useState('');

  const [phone, setPhone] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [newsletterOffers, setNewsletterOffers] = useState(false);
  const [newsletterInsights, setNewsletterInsights] = useState(false);

  const sendCodeFetcher = useFetcherWithPromise();
  const confirmCodeFetcher = useFetcherWithPromise();
  const registrationFetcher = useFetcherWithReset<RegistrationResponse>();
  const loqateFetcher = useFetcherWithPromise();
  const loqateRetrieveFetcher = useFetcherWithPromise();

  const loadingSendCode = LOADING_STATES.includes(sendCodeFetcher.state);
  const loadingConfirm = LOADING_STATES.includes(confirmCodeFetcher.state);
  const loadingSubmit = LOADING_STATES.includes(registrationFetcher.state);
  const loadingOptions = LOADING_STATES.includes(loqateFetcher.state) || LOADING_STATES.includes(loqateRetrieveFetcher.state);

  const loqateErrors = normalizeErrors((loqateFetcher.data as AddressFindData | undefined)?.errors ?? (loqateRetrieveFetcher.data as AddressRetrieveData | undefined)?.errors);
  const combinedErrors = Object.keys(formErrors || {}).length ? formErrors : loqateErrors;
  const addressError = combinedErrors?.addressId ?? combinedErrors?.building ?? combinedErrors?.street ?? combinedErrors?.city ?? combinedErrors?.postalCode;

  useEffect(() => {
    const paramsToSave: Record<string, string> = {};
    TRACKING_PARAM_NAMES.forEach((name) => {
      const value = searchParams.get(name);
      if (value != null) {
        paramsToSave[name] = value;
      }
    });
    if (Object.keys(paramsToSave).length > 0) {
      localStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(paramsToSave));
    }
  }, [searchParams]);

  const formatedAddress = useMemo(() => formatAddressText(manualAddress, retrievedAddress), [manualAddress, retrievedAddress]);
  const isFormDirty = isSignUpFormDirty({
    firstName,
    lastName,
    phone,
    email,
    termsAccepted,
    newsletterOffers,
    newsletterInsights,
    hasAddress: Boolean(retrievedAddress) || Boolean(manualAddress),
  });
  const shouldBlockNavigation = isFormDirty && !showProgress && !verificationResult;
  const leaveFormBlocker = useBlocker(shouldBlockNavigation);

  const startCountdown = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setCountDown(COUNTDOWN_SECONDS);
    // eslint-disable-next-line smells/no-setinterval
    intervalRef.current = setInterval(() => {
      setCountDown((prev) => {
        if (prev <= 1) {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  const fetchAddressOptions = useCallback(
    async (query: string, containerId?: string) => {
      try {
        const response = (await loqateFetcher.submit(
          {
            q: query,
            ...(containerId ? { containerId } : {}),
          },
          { method: 'post', action: '/api/v1/address-find' },
        )) as AddressFindData;
        const addresses = response?.addresses || [];
        setAddressOptions(formatAddressOptions(addresses));
      } catch (error) {
        console.error('Loqate find error:', error);
        setAddressOptions([]);
      }
    },
    [loqateFetcher],
  );

  const handleAddressSelectChange = useCallback(
    async (value: string) => {
      setFormErrors((prev) => ({ ...prev, addressId: undefined, building: undefined, city: undefined, postalCode: undefined, street: undefined }));
      setAddressQ(value);
      if (value.length < ADDRESS_Q_MIN_LENGTH) {
        setAddressOptions([]);
        return;
      }
      await fetchAddressOptions(value);
    },
    [fetchAddressOptions],
  );

  const handleChangeAddress = useCallback(
    async (option: AutoCompleteOptionType<Item> | null) => {
      if (!option?.value || !option.item) return;

      setAddress(option);

      try {
        if (option.item.Type !== 'Address') {
          await fetchAddressOptions(addressQ, option.value);
          return;
        }

        const response = (await loqateRetrieveFetcher.submit({ addressId: option.value }, { method: 'post', action: '/api/v1/address-retrieve' })) as AddressRetrieveData;
        const retrievedData = response?.retrievedData;

        setRetrievedAddress(retrievedData);
        setManualAddress({
          building: getBuilding(retrievedData),
          street: getStreet(retrievedData),
          city: retrievedData?.City ?? '',
          postalCode: retrievedData?.PostalCode ?? '',
          district: retrievedData?.District ?? '',
          country: retrievedData?.CountryName ?? '',
        });
        setFormErrors((prev) => ({ ...prev, addressId: undefined }));
      } catch (error) {
        console.error('Loqate retrieve error:', error);
      }
    },
    [loqateRetrieveFetcher, fetchAddressOptions, addressQ],
  );

  const handleManualAddressChange = useCallback((name: keyof ManualAddress, value: string) => {
    setManualAddress((prev) => ({
      ...(prev ?? { building: '', street: '', city: '', postalCode: '', country: '', district: '' }),
      [name]: value,
    }));
    setAddress(null);
    setRetrievedAddress(undefined);
    setFormErrors((prev) => ({ ...prev, addressId: undefined }));
  }, []);

  const handleSendCode = useCallback(async () => {
    setEmailError(undefined);
    const recaptchaValue = recaptchaRef.current?.getValue() ?? '';
    try {
      await emailValidator(t).validate(email);
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        setEmailError(error.message);
        return;
      }
    }

    const response = (await sendCodeFetcher.submit(
      { email, 'g-recaptcha-response': recaptchaValue },
      { method: 'post', action: '/api/v1/auth/registration/send-code' },
    )) as SendCodeResponse;

    const responseErrors = normalizeErrors(response?.errors);
    if (responseErrors?.recaptcha) {
      toast.error(t(responseErrors.recaptcha));
      recaptchaRef.current?.reset();
      return;
    }

    if (responseErrors?.email) {
      setEmailError(responseErrors.email);
      recaptchaRef.current?.reset();
      return;
    }

    if (responseErrors?.common) {
      toast.error(t(responseErrors.common));
      recaptchaRef.current?.reset();
      return;
    }

    setCodeError(undefined);
    setConfirmationCode(null);
    setCodeModalVisible(true);
    startCountdown();
  }, [email, sendCodeFetcher, startCountdown, t]);

  const confirm = useCallback(
    async (code: string | null) => {
      if (!code || code.length < 4) {
        return;
      }
      if (confirmInFlightRef.current || lastConfirmedCodeRef.current === code || loadingConfirm) {
        return;
      }
      confirmInFlightRef.current = true;
      lastConfirmedCodeRef.current = code;
      setCodeError(undefined);
      try {
        const response = (await confirmCodeFetcher.submit(
          { email: email, code: code ?? null },
          { method: 'post', action: '/api/v1/auth/registration/confirm-code' },
        )) as ConfirmCodeResponse;
        const responseErrors = normalizeErrors(response?.errors);

        if (responseErrors?.email) {
          setEmailError(responseErrors.email);
          setCodeError(responseErrors.email);
          lastConfirmedCodeRef.current = null;
          return;
        }

        if (responseErrors?.code) {
          setCodeError(responseErrors.code);
          lastConfirmedCodeRef.current = null;
          return;
        }

        if (responseErrors?.common) {
          setCodeError(responseErrors.common);
          lastConfirmedCodeRef.current = null;
          return;
        }

        const formData = pendingFormDataRef.current;
        setCodeModalVisible(false);

        if (!formData) {
          return;
        }

        const confirmationHash = response?.confirmationHash ?? '';
        if (confirmationHash) {
          formData.set('confirmationHash', confirmationHash);
        }

        setVerificationResult(null);
        setShowProgress(true);
        registrationFetcher.submit(formData, { method: 'post', action: '/api/v1/auth/registration/complete' });
      } finally {
        confirmInFlightRef.current = false;
      }
    },
    [confirmCodeFetcher, email, loadingConfirm, registrationFetcher],
  );

  const handleCodeChange = useCallback((data: string) => {
    setConfirmationCode(data);
    if (lastConfirmedCodeRef.current && lastConfirmedCodeRef.current !== data) {
      lastConfirmedCodeRef.current = null;
    }
  }, []);

  const handleCodeCompleted = useCallback(
    (data: string) => {
      handleCodeChange(data);
      void confirm(data);
    },
    [confirm, handleCodeChange],
  );

  const handleResend = useCallback(async () => {
    setCodeError(undefined);
    await handleSendCode();
  }, [handleSendCode]);

  const handleContinue = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      const formData = new FormData(event.currentTarget);
      const firstNameValue = getFormValue(formData, 'firstName');
      const lastNameValue = getFormValue(formData, 'lastName');
      const rawBirthDateValue = getFormValue(formData, 'birthDate');
      const normalizedBirthDateValue = toIsoBirthDate(rawBirthDateValue);
      const birthDateValue = normalizedBirthDateValue ?? '';
      formData.set('birthDate', birthDateValue);
      const phoneValue = getFormValue(formData, 'phone');
      const addressIdValue = getFormValue(formData, 'addressId') || null;
      const manualAddressValue =
        !addressIdValue && formData.has('postalCode')
          ? {
              building: getFormValue(formData, 'building'),
              street: getFormValue(formData, 'street'),
              city: getFormValue(formData, 'city'),
              postalCode: getFormValue(formData, 'postalCode'),
              district: getFormValue(formData, 'district'),
              country: getFormValue(formData, 'country'),
            }
          : null;

      const errors: Record<string, string> = {};
      if (rawBirthDateValue.length > 0 && !normalizedBirthDateValue) {
        errors.birthDate = t('screen.gbg.verification.form.birthdate.invalid');
      }

      try {
        await gbgValidator(t).validate(
          { firstName: firstNameValue, lastName: lastNameValue, birthDate: birthDateValue, addressId: addressIdValue, manualAddress: manualAddressValue },
          { abortEarly: false },
        );
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          error.inner.forEach((err) => {
            if (err.path) {
              if (err.path === 'birthDate' && errors.birthDate) {
                return;
              }
              errors[err.path] = err.message;
            }
          });
        }
      }

      if (!addressIdValue) {
        try {
          await gbgManualAddress(t).validate(manualAddressValue, { abortEarly: false });
        } catch (error) {
          if (error instanceof Yup.ValidationError) {
            error.inner.forEach((err) => {
              if (err.path) {
                errors[err.path] = err.message;
              }
            });
          }
        }
      }

      if (!termsAccepted) {
        errors.terms = t('sign.up.terms.not.checked.label');
      }

      const trimmedPhone = phoneValue.trim();
      if (!trimmedPhone) {
        errors.phone = t('sign.up.form.phone.required');
      } else {
        const phoneE164 = normalizePhone(trimmedPhone);
        if (!phoneE164) {
          errors.phone = t('sign.up.form.phone.invalid');
        } else {
          formData.set('phone', phoneE164);
        }
      }

      try {
        await emailValidator(t).validate(email);
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          errors.email = error.message;
        }
      }

      if (Object.keys(errors).length) {
        setFormErrors(errors);
        setShowProgress(false);
        return;
      }

      setFormErrors({});
      const storedAttribution = JSON.parse(localStorage.getItem(ATTRIBUTION_STORAGE_KEY) ?? '{}') as Record<string, string>;
      TRACKING_PARAM_NAMES.forEach((name) => {
        const value = searchParams.get(name) ?? storedAttribution[name];
        if (value != null) {
          formData.set(name, value);
        }
      });
      pendingFormDataRef.current = formData;
      await handleSendCode();
    },
    [email, handleSendCode, searchParams, t, termsAccepted],
  );

  useEffect(() => {
    const data = registrationFetcher.data as RegistrationResponse | undefined;
    if (registrationFetcher.state === 'idle') {
      setShowProgress(false);
      setFormErrors(normalizeErrors(data?.errors) ?? {});
      if (data?.data) {
        setVerificationResult(data.data);
        // Account exists now — apply the two newsletter categories post-registration
        // (keepalive so it survives the redirect into the verification flow). Fire once.
        if (!newsletterSyncedRef.current && (newsletterOffers || newsletterInsights)) {
          newsletterSyncedRef.current = true;
          void fetch('/api/v1/settings/newsletter-categories', {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: new URLSearchParams({ offers: String(newsletterOffers), insights: String(newsletterInsights) }).toString(),
            keepalive: true,
          }).catch(() => undefined);
        }
      }
    }
  }, [registrationFetcher.state, registrationFetcher.data, newsletterOffers, newsletterInsights]);

  useEffect(
    () => () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    },
    [],
  );


  const handleLeaveFormStay = useCallback(() => {
    if (leaveFormBlocker.state !== 'blocked') {
      return;
    }

    leaveFormBlocker.reset();
  }, [leaveFormBlocker]);

  const handleLeaveFormExit = useCallback(async () => {
    if (leaveFormBlocker.state !== 'blocked') {
      return;
    }

    leaveFormBlocker.proceed();
  }, [leaveFormBlocker]);

  const title = t('sign.in.register.label');

  const { maxDate, minDate } = useMemo(() => {
    const maxBirthDate = getBirthMaxDate();
    const minBirthDate = getBirthMinDate();
    return {
      maxDate: format(maxBirthDate, 'yyyy-MM-dd'),
      minDate: format(minBirthDate, 'yyyy-MM-dd'),
    };
  }, []);

  const mobileBirthDateRef = useCallback((input: HTMLInputElement | null) => {
    if (!input) return;

    // iOS Reset uses defaultValue. React otherwise copies the current selection into it on every render.
    const clearDefaultValue = () => {
      if (input.defaultValue) input.defaultValue = '';
    };
    const observer = new MutationObserver(clearDefaultValue);
    observer.observe(input, { attributes: true, attributeFilter: ['value'] });
    clearDefaultValue();

    const handleFocus = () => {
      if (!input.value) {
        // Seed the DOM value before iOS opens its picker; max alone does not set its initial date.
        input.value = maxDate;
        setBirthDate(maxDate);
        setFormErrors((prev) => ({ ...prev, birthDate: undefined }));
      }
    };

    // WebKit date inputs can omit focusin, which React's onFocus relies on.
    input.addEventListener('focus', handleFocus);
    return () => {
      input.removeEventListener('focus', handleFocus);
      observer.disconnect();
    };
  }, [maxDate]);

  const handleMobileGoBack = useCallback(() => {
    if (window.history.state && window.history.state.idx > 0) {
      navigate(-1);
      return;
    }

    navigate('/auth/sign-in');
  }, [navigate]);

  const content = (
    <>
      <Row $justifyContent="center" style={{ marginBottom: 36 }}>
        <Role name="sign-up:title">
          <Typography as="h1" variant="$display03">
            {title}
          </Typography>
        </Role>
      </Row>

      <Role name="sign-up">
        <registrationFetcher.Form method="post" action="/api/v1/auth/registration/complete" onSubmit={handleContinue}>
          <Role name=":name">
            <OutlinedInput
              marginBottom={16}
              id="firstName"
              name="firstName"
              value={firstName}
              label={t('screen.gbg.verification.form.name')}
              errorText={combinedErrors?.firstName}
              onChange={(event) => {
                setFirstName(event.target.value);
                setFormErrors((prev) => ({ ...prev, firstName: undefined }));
              }}
            />
          </Role>

          <Role name=":surname">
            <OutlinedInput
              id="lastName"
              marginBottom={16}
              name="lastName"
              value={lastName}
              label={t('screen.gbg.verification.form.surname')}
              errorText={combinedErrors?.lastName}
              onChange={(event) => {
                setLastName(event.target.value);
                setFormErrors((prev) => ({ ...prev, lastName: undefined }));
              }}
            />
          </Role>

          <Role name=":birth">
            {isMobileScreen ? (
              <MobileBirthDateField>
                <MobileBirthDateInput
                  ref={mobileBirthDateRef}
                  type="date"
                  name="birthDate"
                  id="birthDate"
                  autoComplete="bday"
                  value={birthDate}
                  label={t('screen.gbg.verification.form.date.of.birth')}
                  errorText={combinedErrors?.birthDate}
                  max={maxDate}
                  min={minDate}
                  onChange={(event) => {
                    setBirthDate(event.target.value);
                    setFormErrors((prev) => ({ ...prev, birthDate: undefined }));
                    // iOS Reset clears the input but leaves the picker showing its previous selection.
                    if (!event.target.value) {
                      event.currentTarget.blur();
                    }
                  }}
                />
                <MobileBirthDateIcon aria-hidden="true" focusable="false" />
              </MobileBirthDateField>
            ) : (
              <DesktopBirthDateInput
                name="birthDate"
                id="birthDate"
                label={t('screen.gbg.verification.form.date.of.birth')}
                errorText={combinedErrors?.birthDate}
                onChange={(_value) => {
                  setFormErrors((prev) => ({ ...prev, birthDate: undefined }));
                }}
              />
            )}
          </Role>

          <Role name=":phone">
            <OutlinedInput
              id="phone"
              marginBottom={16}
              name="phone"
              type="tel"
              value={phone}
              label={t('sign.up.form.phone.label')}
              errorText={combinedErrors?.phone}
              onChange={(event) => {
                setPhone(sanitizePhoneInput(event.target.value));
                setFormErrors((prev) => ({ ...prev, phone: undefined }));
              }}
            />
          </Role>

          <input name="addressId" hidden value={retrievedAddress?.Id ?? ''} readOnly />
          <input name="building" hidden value={manualAddress?.building ?? ''} readOnly />
          <input name="street" hidden value={manualAddress?.street ?? ''} readOnly />
          <input name="city" hidden value={manualAddress?.city ?? ''} readOnly />
          <input name="postalCode" hidden value={manualAddress?.postalCode ?? ''} readOnly />

          <Role name=":address">
            <AutoComplete
              bordered
              id="selectAddress"
              name="selectAddress"
              autoComplete="disabled"
              options={addressOptions}
              onRequestOptions={handleAddressSelectChange}
              onChange={handleChangeAddress}
              onClear={() => {
                setAddress(null);
                setManualAddress(null);
                setRetrievedAddress(undefined);
                setFormErrors((prev) => ({ ...prev, addressId: undefined, building: undefined, city: undefined, postalCode: undefined, street: undefined }));
              }}
              value={address}
              label={t('screen.gbg.verification.form.current.address')}
              messageEmpty={t('screen.gbg.verification.address.no.results')}
              error={!!addressError}
              errorText={addressError}
              updateInputOnSelect={false}
              minLength={6}
              endIcon={loadingOptions ? <Spinner color={'$label02' as ColorName} /> : null}
              emptyContent={
                loadingOptions && !addressError ? null : (
                  <Role name=":manual">
                    {/* <AddressActions> */}
                    <Button
                      type="button"
                      variant="ghost"
                      size="medium"
                      onClick={() => setManualModalVisible(true)}
                      style={{
                        backgroundColor: colors.$ui01,
                        borderTopLeftRadius: 0,
                        borderTopRightRadius: 0,
                        borderTopWidth: '1px',
                        borderTopStyle: 'solid',
                        borderTopColor: colors.$label03,
                      }}>
                      <Typography variant={'$button03'} color={'$label02'}>
                        {t('screen.gbg.verification.form.current.address.enter.manually')}
                      </Typography>
                    </Button>
                    {/* </AddressActions> */}
                  </Role>
                )
              }
              startIcon={<IconSearch16 />}
            />
          </Role>

          {formatedAddress ? <ReadOnlyTextarea value={formatedAddress} onPressEdit={() => setManualModalVisible(true)} bordered marginBottom={20} /> : null}

          <Role name=":email">
            <OutlinedInput
              marginBottom={16}
              name="email"
              type="text"
              value={email}
              onChange={(event) => {
                setEmail(event.target.value);
                setEmailError(undefined);
              }}
              errorText={emailError || combinedErrors?.email}
              label={t('sign.in.form.email.label')}
              autoComplete="username email"
              id={'email'}
            />
          </Role>

          <Role name=":terms-checkbox">
            <Checkbox
              name="terms"
              value={termsAccepted ? 'checked' : ''}
              checked={termsAccepted}
              onChange={(event) => {
                setTermsAccepted(event.target.checked);
                setFormErrors((prev) => ({ ...prev, terms: undefined }));
              }}
              children={
                <Role name=":terms">
                  <Typography as="div" color="$secondaryText" variant="$body02">
                    <Trans i18nKey="sign.up.terms.web.age">
                      <Link to="https://www.copybet.com/terms-and-conditions/" className="text-uppercase underline" target="_blank" />
                    </Trans>
                  </Typography>
                </Role>
              }
              className={combinedErrors?.terms ? 'checkbox-error' : 'checkbox-regular'}
            />
          </Role>
          {combinedErrors?.terms ? (
            <Typography as="div" variant="$caption02" color="$red" style={{ marginTop: 4 }}>
              {combinedErrors.terms}
            </Typography>
          ) : null}

          <Role name=":subscribe-offers-checkbox" style={{ marginTop: 16 }}>
            <Checkbox
              name="newsletterOffers"
              value={newsletterOffers ? 'checked' : ''}
              checked={newsletterOffers}
              onChange={(event) => setNewsletterOffers(event.target.checked)}
              children={
                <Role name=":subscribe-offers">
                  <Typography as="div" color="$secondaryText" variant="$body02">
                    <Trans i18nKey="sign.up.newsletter.consent.offers">
                      <strong />
                      <Link to="https://www.copybet.com/offers/" className="underline" target="_blank" />
                    </Trans>
                  </Typography>
                </Role>
              }
            />
          </Role>

          <Role name=":subscribe-insights-checkbox" style={{ marginTop: 16 }}>
            <Checkbox
              name="newsletterInsights"
              value={newsletterInsights ? 'checked' : ''}
              checked={newsletterInsights}
              onChange={(event) => setNewsletterInsights(event.target.checked)}
              children={
                <Role name=":subscribe-insights">
                  <Typography as="div" color="$secondaryText" variant="$body02">
                    <Trans i18nKey="sign.up.newsletter.consent.insights" />
                  </Typography>
                </Role>
              }
            />
          </Role>
          <Role name=":recaptcha">
            <Row $justifyContent="flex-start" $alignContent="center" $alignItems="center" style={{ marginTop: 8, marginBottom: 16 }}>
              <ReCAPTCHA sitekey={loaderData.rkey} ref={recaptchaRef} />
            </Row>
          </Role>

          <Role name=":submit">
            <Button
              style={{ marginBottom: 24 }}
              data-testid="login"
              loading={loadingSendCode || loadingSubmit}
              type="submit"
              name="continue_registration"
              variant="primary"
              size="large"
              fullWidth>
              {t('sign.up.button.continue')}
            </Button>
          </Role>
        </registrationFetcher.Form>
      </Role>

      <Modal
        visible={codeModalVisible}
        canCloseOnOutside={false}
        onClose={() => {
          setCodeModalVisible(false);
        }}
        closeLabel={t('notice.close')}
        content={
          <Role name="sign-up-confirmation">
            <Form method="post">
              <Typography as="h1" variant="$display03" align="center" style={{ marginBottom: 24 }}>
                {t('confirmation.email.sing.access.code')}
              </Typography>

              <Typography as="div" variant="$body02" color="$label02" align="center">
                {t('confirmation.description.1')}
              </Typography>
              <Typography as="div" variant="$body02" align="center">
                {email}
              </Typography>
              <Typography as="div" variant="$body02" color="$label02" align="center" style={{ marginBottom: 24 }}>
                {t('confirmation.description.2')}
              </Typography>

              <ConfirmationCodeInput
                state={confirmCodeFetcher.state}
                error={codeError ? { error: true, message: codeError } : undefined}
                inputProps={{ required: true, name: 'code' }}
                length={4}
                onChange={handleCodeChange}
                onCompleted={handleCodeCompleted}
                onClear={() => {
                  setConfirmationCode(null);
                  lastConfirmedCodeRef.current = null;
                  setCodeError(undefined);
                }}
              />

              <Role name="sign-in-confirmation:confirm">
                <Button
                  data-testid="confirm"
                  loading={loadingConfirm}
                  type="button"
                  name="confirm"
                  variant="primary"
                  size="large"
                  fullWidth
                  style={{ marginTop: 24, marginBottom: 16 }}
                  onClick={() => confirm(confirmationCode)}>
                  {t('confirmation.button.confirm')}
                </Button>
              </Role>

              {countDown > 0 ? (
                <Typography as="div" variant="$body02" color="$label02" align="center">
                  <Trans i18nKey="confirmation.sign.in.button.resend.in.v2" values={{ seconds: countDown }}>
                    <br />
                  </Trans>
                </Typography>
              ) : (
                <Button variant="ghost" size="medium" type="button" onClick={handleResend} fullWidth loading={loadingSendCode}>
                  {t('confirmation.button.resend')}
                </Button>
              )}
            </Form>
          </Role>
        }
      />

      <Modal
        visible={manualModalVisible}
        onClose={() => setManualModalVisible(false)}
        content={<InstantVerificationManuallyAddress values={manualAddress ?? undefined} onChange={handleManualAddressChange} onClose={() => setManualModalVisible(false)} />}
      />

      <Modal
        visible={showProgress && !verificationResult}
        canCloseOnOutside={false}
        onClose={() => setShowProgress(false)}
        content={<InstantVerificationProgress loading={showProgress} />}
      />

      <Modal
        visible={leaveFormBlocker.state === 'blocked'}
        onClose={handleLeaveFormStay}
        canCloseOnOutside={false}
        title={
          <Row $alignItems="center" $gap={8}>
            <VerificationRequiredIcon />
            <Role name="sign-up:leave-form-title">
              <Typography variant="$heading04" color="$label02" style={{ textTransform: 'uppercase' }}>
                {t('sign.up.exit.confirm.title')}
              </Typography>
            </Role>
          </Row>
        }
        content={
          <Role name="sign-up:leave-form">
            <>
              <Typography as="p" variant="$body02" color="$label02">
                {t('sign.up.exit.confirm.subtitle')}
              </Typography>
              <LeaveModalActions>
                <Button type="button" variant="primary" size="large" fullWidth onClick={handleLeaveFormStay}>
                  {t('sign.up.exit.confirm.stay')}
                </Button>
                <Button type="button" variant="secondary" size="large" fullWidth onClick={handleLeaveFormExit}>
                  {t('sign.up.exit.confirm.exit')}
                </Button>
              </LeaveModalActions>
            </>
          </Role>
        }
      />
    </>
  );

  return isMobileScreen ? (
    <WidgetStack backgroundColor="$ui02" title="" goBack={handleMobileGoBack}>
      {content}
    </WidgetStack>
  ) : (
    content
  );
}
